"use client";

import { useEffect, useRef } from "react";
import { HmcIcon, HmcButton } from "@adi-ctx/harmonic-core-components-react";
import { RiLogoutBoxRLine } from "@remixicon/react";
import { useAuth } from "@/components";
import type { TAuthContextType } from "@/components";
import {
	COGNITO_ACCESS_TOKEN,
	COGNITO_REFRESH_TOKEN,
	LOGIN_URL,
} from "@/lib/constants/auth.constants";
import { t } from "@/utils";
import authApi, { getCognitoToken } from "@/lib/api/auth.api";

export default function HeaderUser() {
	const pingAuth = useRef<number | undefined>(undefined);

	const {
		cognitoUserInfo: userInfo,
		cognitoFullLogout: logout,
		removeCognitoStorageItems: clearStorageItems,
		saveCognitoStorageItems: saveStorageItems,
		isAuthenticated,
		isAuthenticatingInProgress,
	}: TAuthContextType = useAuth();

	useEffect(() => {
		if (isAuthenticatingInProgress) return;

		if (isAuthenticated) {
			/* every 70 minutes check the auth status and refresh the token */
			pingAuth.current = window.setInterval(() => {
				authApi
					.getCognitoUserInfo(localStorage.getItem(COGNITO_ACCESS_TOKEN)!)
					.catch(() => {
						// try to refresh the token
						const refreshToken = localStorage.getItem(COGNITO_REFRESH_TOKEN);
						authApi
							.getCognitoToken({
								urlCode: refreshToken!,
								refreshToken: refreshToken!,
							})
							.then((response) => {
								// save the new tokens
								saveStorageItems(response);
							})
							.catch(() => {
								clearStorageItems();
								window.clearInterval(pingAuth.current);
							});
					});
			}, 4200000);
		} else {
			window.clearInterval(pingAuth.current);
		}

		return () => {
			window.clearInterval(pingAuth.current);
		};
	}, [
		isAuthenticated,
		isAuthenticatingInProgress,
		clearStorageItems,
		saveStorageItems,
	]);

	return (
		<>
			{isAuthenticated && (
				<div className="hmc-body-xs ml-2 flex items-center whitespace-nowrap">
					<span
						data-testid="header-navigation-user"
						className="text-sm font-semibold"
					>
						{userInfo?.givenName} {userInfo?.familyName}
					</span>
					<HmcButton
						isIconOnly
						kind="ghost"
						title={t("auth.logout")}
						data-testid="header-navigation-button-logout"
						className="ml-2"
						onClick={logout}
					>
						<RiLogoutBoxRLine data-testid="header-navigation-icon-logout" />
						<span className="hidden">{t("auth.logout")}</span>
					</HmcButton>
				</div>
			)}

			{!isAuthenticated && (
				<a title={t("auth.login")} href={LOGIN_URL}>
					<HmcButton
						isIconOnly
						isDisabled={isAuthenticatingInProgress}
						kind="ghost"
						data-testid="header-navigation-button-login"
						title={t("auth.login")}
					>
						<HmcIcon
							name="user-m"
							data-testid="header-navigation-button-login-icon"
						/>
						<span className="hidden">{t("auth.login")}</span>
					</HmcButton>
				</a>
			)}
		</>
	);
}
